/* eslint-disable no-nested-ternary, no-restricted-syntax, no-continue, new-cap, no-unused-vars */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Strategic Report PDF Generator
 *
 * Design principles:
 *  - Clear visual hierarchy (section headers, KPI cards, body text)
 *  - Scanability: every number has a label and contextual comparison
 *  - Consistent margins / grid / color palette
 *  - No charts as images - everything is a table or text (searchable, sharp at any zoom)
 *  - Action-oriented: strengths/weaknesses/recommendations clearly separated
 */

// ───────────────────────────────────────────────────────────────────────────
// Design tokens
// ───────────────────────────────────────────────────────────────────────────
const PALETTE = {
  primary: [37, 99, 235], // blue-600
  primaryDk: [30, 64, 175], // blue-800
  indigo: [79, 70, 229], // indigo-600
  ink: [15, 23, 42], // slate-900 - main text
  body: [30, 41, 59], // slate-800 - paragraph
  muted: [100, 116, 139], // slate-500 - secondary text
  line: [226, 232, 240], // slate-200 - borders
  bgSoft: [248, 250, 252], // slate-50 - zebra
  bgSubtle: [241, 245, 249], // slate-100 - zebra stronger
  positive: [5, 150, 105], // emerald-600
  positiveBg: [209, 250, 229], // emerald-100
  positiveSoft: [236, 253, 245],
  negative: [220, 38, 38], // red-600
  negativeBg: [254, 226, 226], // red-100
  negativeSoft: [255, 241, 242],
  neutral: [100, 116, 139], // slate-500
  neutralBg: [226, 232, 240],
  amber: [217, 119, 6],
  amberBg: [254, 243, 199],
  white: [255, 255, 255],
};

const PAGE = { width: 210, height: 297 };
const MARGIN = 14;
const CONTENT_W = PAGE.width - MARGIN * 2;
const BOTTOM_SAFE = 285; // y below which we must paginate

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────
const fmt = {
  int(n) {
    return Intl.NumberFormat('pt-BR').format(Number(n) || 0);
  },
  pct(n, digits = 1) {
    return `${(Number(n) || 0).toFixed(digits)}%`;
  },
  signedPct(n, digits = 1) {
    const v = Number(n) || 0;
    return `${v > 0 ? '+' : ''}${v.toFixed(digits)}%`;
  },
  date(d) {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleString('pt-BR');
    } catch {
      return String(d);
    }
  },
  shortDate(d) {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      });
    } catch {
      return String(d);
    }
  },
  truncate(s, n = 90) {
    const str = (s || '').toString().replace(/\s+/g, ' ').trim();
    return str.length > n ? `${str.slice(0, n - 1)}...` : str;
  },
};

function drawPageChrome(doc, pageIndex, totalPages, meta) {
  // Top band
  doc.setFillColor(...PALETTE.primary);
  doc.rect(0, 0, PAGE.width, 8, 'F');
  doc.setFillColor(...PALETTE.primaryDk);
  doc.rect(0, 8, PAGE.width, 0.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...PALETTE.white);
  doc.text('REDES SOCIAIS - RELATÓRIO ESTRATÉGICO', MARGIN, 5.5);

  doc.setFont('helvetica', 'normal');
  const profileName = meta?.profile?.name ? ` · ${meta.profile.name}` : '';
  doc.text(
    `${meta?.period ? `${meta.period}d` : ''}${profileName}`,
    PAGE.width - MARGIN,
    5.5,
    { align: 'right' }
  );

  // Footer
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...PALETTE.muted);
  doc.text(
    `Gerado em ${new Date().toLocaleString('pt-BR')}`,
    MARGIN,
    PAGE.height - 6
  );
  doc.text(
    `${pageIndex} / ${totalPages}`,
    PAGE.width - MARGIN,
    PAGE.height - 6,
    { align: 'right' }
  );

  // Accent footer line
  doc.setDrawColor(...PALETTE.line);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, PAGE.height - 10, PAGE.width - MARGIN, PAGE.height - 10);
}

function ensureRoom(doc, cursorY, needed) {
  if (cursorY + needed > BOTTOM_SAFE) {
    doc.addPage();
    return 22;
  }
  return cursorY;
}

function sectionTitle(doc, y, { eyebrow, title, description }) {
  y = ensureRoom(doc, y, 28);

  // Thin colored rule left
  doc.setFillColor(...PALETTE.primary);
  doc.rect(MARGIN, y, 1.5, 10, 'F');

  if (eyebrow) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...PALETTE.primary);
    doc.text(eyebrow.toUpperCase(), MARGIN + 4, y + 3.5);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...PALETTE.ink);
  doc.text(title, MARGIN + 4, y + 9);

  let nextY = y + 13;

  if (description) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...PALETTE.muted);
    const lines = doc.splitTextToSize(description, CONTENT_W - 4);
    doc.text(lines, MARGIN + 4, nextY);
    nextY += lines.length * 4 + 2;
  }

  return nextY + 3;
}

function drawKpiCards(doc, y, kpis) {
  // kpis: Array<{ label, value, hint?, color? }>
  const n = kpis.length;
  const gap = 3;
  const totalW = CONTENT_W;
  const cardW = (totalW - gap * (n - 1)) / n;
  const cardH = 22;

  y = ensureRoom(doc, y, cardH + 5);

  kpis.forEach((kpi, i) => {
    const x = MARGIN + i * (cardW + gap);

    // Card background
    doc.setFillColor(...PALETTE.bgSoft);
    doc.roundedRect(x, y, cardW, cardH, 2, 2, 'F');

    // Left accent
    const accent = kpi.color || PALETTE.primary;
    doc.setFillColor(...accent);
    doc.rect(x, y, 1.5, cardH, 'F');

    // Label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...PALETTE.muted);
    doc.text(kpi.label.toUpperCase(), x + 4, y + 5);

    // Value
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...(kpi.valueColor || PALETTE.ink));
    doc.text(String(kpi.value), x + 4, y + 13);

    // Hint
    if (kpi.hint) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...PALETTE.muted);
      doc.text(kpi.hint, x + 4, y + 18);
    }
  });

  return y + cardH + 4;
}

function drawSentimentBar(doc, y, { positive, negative, neutral }) {
  const total = positive + negative + neutral;
  if (total === 0) return y;

  y = ensureRoom(doc, y, 14);

  const barH = 5;
  const barY = y;
  const pP = positive / total;
  const pN = negative / total;
  const pNeu = neutral / total;

  let x = MARGIN;

  // Background
  doc.setFillColor(...PALETTE.bgSubtle);
  doc.roundedRect(MARGIN, barY, CONTENT_W, barH, 1, 1, 'F');

  // Segments
  if (pP > 0) {
    const w = CONTENT_W * pP;
    doc.setFillColor(...PALETTE.positive);
    doc.rect(x, barY, w, barH, 'F');
    x += w;
  }
  if (pN > 0) {
    const w = CONTENT_W * pN;
    doc.setFillColor(...PALETTE.negative);
    doc.rect(x, barY, w, barH, 'F');
    x += w;
  }
  if (pNeu > 0) {
    const w = CONTENT_W * pNeu;
    doc.setFillColor(...PALETTE.neutral);
    doc.rect(x, barY, w, barH, 'F');
  }

  // Legend
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  let lx = MARGIN;
  const legendY = barY + barH + 4.5;

  const legend = [
    {
      label: `Positivo: ${fmt.int(positive)} (${fmt.pct(pP * 100, 0)})`,
      color: PALETTE.positive,
    },
    {
      label: `Negativo: ${fmt.int(negative)} (${fmt.pct(pN * 100, 0)})`,
      color: PALETTE.negative,
    },
    {
      label: `Neutro: ${fmt.int(neutral)} (${fmt.pct(pNeu * 100, 0)})`,
      color: PALETTE.neutral,
    },
  ];

  legend.forEach(item => {
    doc.setFillColor(...item.color);
    doc.circle(lx + 1, legendY - 1, 1.1, 'F');
    doc.setTextColor(...PALETTE.body);
    doc.text(item.label, lx + 3.5, legendY);
    lx += doc.getTextWidth(item.label) + 10;
  });

  return legendY + 4;
}

function drawCallout(
  doc,
  y,
  { title, body, color = PALETTE.primary, bg = [239, 246, 255] }
) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const lines = doc.splitTextToSize(body, CONTENT_W - 10);
  const h = 10 + lines.length * 5 + 4;

  y = ensureRoom(doc, y, h + 2);

  doc.setFillColor(...bg);
  doc.roundedRect(MARGIN, y, CONTENT_W, h, 2, 2, 'F');
  doc.setFillColor(...color);
  doc.rect(MARGIN, y, 2, h, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...color);
  doc.text(title.toUpperCase(), MARGIN + 6, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...PALETTE.body);
  doc.text(lines, MARGIN + 6, y + 11);

  return y + h + 3;
}

function bulletList(doc, y, items = [], color = PALETTE.primary) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...PALETTE.body);

  items.forEach(raw => {
    const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
    const lines = doc.splitTextToSize(text, CONTENT_W - 8);
    const h = lines.length * 4.8 + 1;
    y = ensureRoom(doc, y, h);

    // Dot
    doc.setFillColor(...color);
    doc.circle(MARGIN + 2.5, y + 1.4, 1, 'F');

    doc.setTextColor(...PALETTE.body);
    doc.text(lines, MARGIN + 6, y + 2);
    y += h;
  });
  return y + 2;
}

// ───────────────────────────────────────────────────────────────────────────
// Sections
// ───────────────────────────────────────────────────────────────────────────
function renderCover(doc, { report, profile, context }) {
  // Full-page gradient
  const steps = 40;
  for (let i = 0; i < steps; i += 1) {
    const t = i / steps;
    const r = Math.round(PALETTE.primary[0] * (1 - t) + PALETTE.indigo[0] * t);
    const g = Math.round(PALETTE.primary[1] * (1 - t) + PALETTE.indigo[1] * t);
    const b = Math.round(PALETTE.primary[2] * (1 - t) + PALETTE.indigo[2] * t);
    doc.setFillColor(r, g, b);
    doc.rect(
      0,
      (PAGE.height * i) / steps,
      PAGE.width,
      PAGE.height / steps + 0.5,
      'F'
    );
  }

  // Decorative corner
  doc.setFillColor(255, 255, 255);
  doc.setGState(new doc.GState({ opacity: 0.08 }));
  doc.circle(PAGE.width - 20, 40, 60, 'F');
  doc.circle(25, PAGE.height - 40, 80, 'F');
  doc.setGState(new doc.GState({ opacity: 1 }));

  doc.setTextColor(...PALETTE.white);

  // Eyebrow
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('REDES SOCIAIS', MARGIN, 36);

  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, 38, MARGIN + 20, 38);

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  const title = doc.splitTextToSize(
    report?.title || 'Relatório Estratégico',
    CONTENT_W
  );
  doc.text(title, MARGIN, 56);

  // Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  const subtitle = [
    profile?.name ? `Perfil: ${profile.name}` : null,
    context?.period ? `Período: últimos ${context.period} dias` : null,
    context?.platform && context.platform !== 'Todas'
      ? `Plataforma: ${context.platform}`
      : null,
  ]
    .filter(Boolean)
    .join('  ·  ');
  if (subtitle) {
    doc.text(subtitle, MARGIN, 56 + title.length * 10);
  }

  // Key stats block (if summary available)
  if (context?.summary) {
    const s = context.summary;
    const sent = s.sentiment || {};
    const eng = s.engagement || {};
    const total =
      (sent.positive || 0) + (sent.negative || 0) + (sent.neutral || 0);
    const pctPos = total ? Math.round((sent.positive / total) * 100) : 0;

    const boxY = 120;
    const boxH = 75;

    doc.setFillColor(255, 255, 255);
    doc.setGState(new doc.GState({ opacity: 0.15 }));
    doc.roundedRect(MARGIN, boxY, CONTENT_W, boxH, 4, 4, 'F');
    doc.setGState(new doc.GState({ opacity: 1 }));

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...PALETTE.white);
    doc.text('PANORAMA', MARGIN + 6, boxY + 8);

    const stats = [
      { label: 'Menções', value: fmt.int(s.totalMentions) },
      { label: 'Positivas', value: `${pctPos}%` },
      { label: 'Engajamento', value: fmt.int(eng.total || 0) },
      { label: 'Tendência', value: fmt.signedPct(s.trend, 1) },
    ];
    const colW = (CONTENT_W - 12) / stats.length;
    stats.forEach((st, i) => {
      const cx = MARGIN + 6 + i * colW;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      doc.setTextColor(...PALETTE.white);
      doc.text(st.value, cx, boxY + 32);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(st.label.toUpperCase(), cx, boxY + 40);
    });
  }

  // Footer info
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text(
    `Gerado em ${fmt.date(report?.createdAt || new Date())}`,
    MARGIN,
    PAGE.height - 20
  );
  doc.text('CHATBOOT INNOVATS', PAGE.width - MARGIN, PAGE.height - 20, {
    align: 'right',
  });
}

function renderExecutiveSummary(doc, y, { context }) {
  if (!context?.summary) return y;

  y = sectionTitle(doc, y, {
    eyebrow: 'Panorama',
    title: 'Resumo Executivo',
    description:
      'Os números principais do período analisado, com comparativo contra o período anterior.',
  });

  const s = context.summary;
  const sent = s.sentiment || {};
  const eng = s.engagement || {};
  const total =
    (sent.positive || 0) + (sent.negative || 0) + (sent.neutral || 0);
  const pctPos = total ? (sent.positive / total) * 100 : 0;
  const pctNeg = total ? (sent.negative / total) * 100 : 0;

  y = drawKpiCards(doc, y, [
    {
      label: 'Menções totais',
      value: fmt.int(s.totalMentions),
      color: PALETTE.primary,
    },
    {
      label: '% Positivo',
      value: `${pctPos.toFixed(0)}%`,
      color: PALETTE.positive,
      valueColor: PALETTE.positive,
    },
    {
      label: '% Negativo',
      value: `${pctNeg.toFixed(0)}%`,
      color: PALETTE.negative,
      valueColor: PALETTE.negative,
    },
    {
      label: 'Tendência',
      value: fmt.signedPct(s.trend, 1),
      color: s.trend >= 0 ? PALETTE.positive : PALETTE.negative,
      valueColor: s.trend >= 0 ? PALETTE.positive : PALETTE.negative,
      hint: 'vs período anterior',
    },
  ]);

  y = drawKpiCards(doc, y, [
    { label: 'Curtidas', value: fmt.int(eng.likes), color: PALETTE.primary },
    {
      label: 'Comentários',
      value: fmt.int(eng.comments),
      color: PALETTE.indigo,
    },
    {
      label: 'Compartilhamentos',
      value: fmt.int(eng.shares),
      color: PALETTE.amber,
    },
    {
      label: 'Engajamento total',
      value: fmt.int(eng.total),
      color: PALETTE.primaryDk,
    },
  ]);

  // Sentiment distribution bar
  y = drawSentimentBar(doc, y + 2, sent);

  return y + 4;
}

function renderSentimentTable(doc, y, { context }) {
  if (!context?.summary?.sentiment) return y;

  const s = context.summary.sentiment;
  const total = s.positive + s.negative + s.neutral;
  if (!total) return y;

  y = sectionTitle(doc, y, {
    eyebrow: 'Sentimento',
    title: 'Distribuição de Sentimento',
  });

  const rows = [
    {
      label: 'Positivo',
      count: s.positive,
      pct: (s.positive / total) * 100,
      color: PALETTE.positive,
    },
    {
      label: 'Negativo',
      count: s.negative,
      pct: (s.negative / total) * 100,
      color: PALETTE.negative,
    },
    {
      label: 'Neutro',
      count: s.neutral,
      pct: (s.neutral / total) * 100,
      color: PALETTE.neutral,
    },
  ];

  autoTable(doc, {
    startY: y,
    head: [['Sentimento', 'Menções', '% do total', 'Distribuição']],
    body: [
      ...rows.map(r => [r.label, fmt.int(r.count), fmt.pct(r.pct, 1), '']),
      [
        {
          content: 'Total',
          styles: { fontStyle: 'bold', textColor: PALETTE.ink },
        },
        {
          content: fmt.int(total),
          styles: { fontStyle: 'bold', halign: 'right' },
        },
        { content: '100%', styles: { halign: 'right', fontStyle: 'bold' } },
        '',
      ],
    ],
    theme: 'grid',
    styles: {
      fontSize: 10,
      cellPadding: 3,
      textColor: PALETTE.body,
      lineColor: PALETTE.line,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: PALETTE.primaryDk,
      textColor: PALETTE.white,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 3,
    },
    alternateRowStyles: { fillColor: PALETTE.bgSoft },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40 },
      1: { halign: 'right', cellWidth: 30 },
      2: { halign: 'right', cellWidth: 30 },
      3: { cellWidth: 'auto' },
    },
    margin: { left: MARGIN, right: MARGIN },
    didParseCell: hookData => {
      if (hookData.section !== 'body' || hookData.row.index === 3) return;
      if (hookData.column.index === 0) {
        const row = hookData.row.raw[0];
        if (row === 'Positivo')
          hookData.cell.styles.textColor = PALETTE.positive;
        if (row === 'Negativo')
          hookData.cell.styles.textColor = PALETTE.negative;
        if (row === 'Neutro') hookData.cell.styles.textColor = PALETTE.neutral;
      }
    },
    // Draw a real proportional bar in the last column
    didDrawCell: hookData => {
      if (hookData.section !== 'body' || hookData.column.index !== 3) return;
      if (hookData.row.index >= rows.length) return;
      const row = rows[hookData.row.index];
      const pad = 2;
      const cx = hookData.cell.x + pad;
      const cy = hookData.cell.y + hookData.cell.height / 2 - 1.75;
      const maxW = hookData.cell.width - pad * 2;
      const barW = Math.max(0.5, (row.pct / 100) * maxW);

      // Track (background)
      doc.setFillColor(...PALETTE.bgSubtle);
      doc.roundedRect(cx, cy, maxW, 3.5, 0.8, 0.8, 'F');
      // Fill
      doc.setFillColor(...row.color);
      doc.roundedRect(cx, cy, barW, 3.5, 0.8, 0.8, 'F');
    },
  });

  return doc.lastAutoTable.finalY + 6;
}

function renderSentimentTimeline(doc, y, { context }) {
  const rows = Array.isArray(context?.sentiment) ? context.sentiment : [];
  const withData = rows.filter(r => r.positive || r.negative || r.neutral);
  if (!withData.length) return y;

  y = sectionTitle(doc, y, {
    eyebrow: 'Evolução Temporal',
    title: 'Sentimento Diário',
    description:
      'Volume de menções classificadas por sentimento em cada dia do período.',
  });

  // Only show days with activity, last ~20
  const show = withData.slice(-20);

  autoTable(doc, {
    startY: y,
    head: [['Data', 'Positivo', 'Negativo', 'Neutro', 'Total']],
    body: show.map(r => {
      const t = (r.positive || 0) + (r.negative || 0) + (r.neutral || 0);
      return [
        fmt.shortDate(r.date),
        {
          content: fmt.int(r.positive),
          styles: { textColor: PALETTE.positive, halign: 'right' },
        },
        {
          content: fmt.int(r.negative),
          styles: { textColor: PALETTE.negative, halign: 'right' },
        },
        {
          content: fmt.int(r.neutral),
          styles: { textColor: PALETTE.neutral, halign: 'right' },
        },
        { content: fmt.int(t), styles: { halign: 'right', fontStyle: 'bold' } },
      ];
    }),
    theme: 'grid',
    styles: {
      fontSize: 8.5,
      cellPadding: 2,
      textColor: PALETTE.body,
      lineColor: PALETTE.line,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: PALETTE.primaryDk,
      textColor: PALETTE.white,
      fontSize: 8,
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: PALETTE.bgSoft },
    margin: { left: MARGIN, right: MARGIN },
  });

  return doc.lastAutoTable.finalY + 6;
}

function renderTopics(doc, y, { context }) {
  const topics = Array.isArray(context?.topics) ? context.topics : [];
  if (!topics.length) return y;

  y = sectionTitle(doc, y, {
    eyebrow: 'Temas',
    title: 'Distribuição de Temas',
    description:
      'Temas políticos mais presentes nas menções, com a decomposição de sentimento de cada um.',
  });

  const top = topics.slice(0, 15);

  autoTable(doc, {
    startY: y,
    head: [
      [
        '#',
        'Tema',
        'Total',
        'Positivo',
        'Negativo',
        'Neutro',
        'Sentimento dominante',
      ],
    ],
    body: top.map((t, i) => {
      const max = Math.max(t.positive, t.negative, t.neutral);
      let dominant = 'neutro';
      let dominantColor = PALETTE.neutral;
      if (max === t.positive && max > 0) {
        dominant = 'positivo';
        dominantColor = PALETTE.positive;
      } else if (max === t.negative && max > 0) {
        dominant = 'negativo';
        dominantColor = PALETTE.negative;
      }
      return [
        String(i + 1),
        t.topic,
        {
          content: fmt.int(t.count),
          styles: { halign: 'right', fontStyle: 'bold' },
        },
        {
          content: fmt.int(t.positive),
          styles: { halign: 'right', textColor: PALETTE.positive },
        },
        {
          content: fmt.int(t.negative),
          styles: { halign: 'right', textColor: PALETTE.negative },
        },
        {
          content: fmt.int(t.neutral),
          styles: { halign: 'right', textColor: PALETTE.neutral },
        },
        {
          content: dominant,
          styles: { textColor: dominantColor, fontStyle: 'bold' },
        },
      ];
    }),
    theme: 'grid',
    styles: {
      fontSize: 8.5,
      cellPadding: 2.5,
      textColor: PALETTE.body,
      lineColor: PALETTE.line,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: PALETTE.primaryDk,
      textColor: PALETTE.white,
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: PALETTE.bgSoft },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center', textColor: PALETTE.muted },
      1: { fontStyle: 'bold' },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  return doc.lastAutoTable.finalY + 6;
}

function renderTopPosts(doc, y, { context }) {
  const posts = context?.engagement?.topPosts || [];
  if (!posts.length) return y;

  y = sectionTitle(doc, y, {
    eyebrow: 'Engajamento',
    title: 'Top Posts',
    description: 'Publicações com maior engajamento no período.',
  });

  // Show averages first
  const avg = context.engagement.averages || {};
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...PALETTE.muted);
  doc.text(
    `Médias por post: ${fmt.int(Math.round(avg.likes))} curtidas  ·  ${fmt.int(Math.round(avg.comments))} comentários  ·  ${fmt.int(Math.round(avg.shares))} compartilhamentos`,
    MARGIN,
    y
  );
  y += 5;

  const sentimentLabel = s => {
    if (s === 'positive') return 'Positivo';
    if (s === 'negative') return 'Negativo';
    if (s === 'neutral') return 'Neutro';
    return '-';
  };

  autoTable(doc, {
    startY: y,
    head: [
      [
        '#',
        'Autor',
        'Conteúdo',
        'Sentimento',
        'Curtidas',
        'Coment.',
        'Compart.',
      ],
    ],
    body: posts.slice(0, 8).map((p, i) => [
      String(i + 1),
      fmt.truncate(p.authorName, 20),
      fmt.truncate(p.content, 80),
      {
        content: sentimentLabel(p.sentiment),
        styles: {
          textColor:
            p.sentiment === 'positive'
              ? PALETTE.positive
              : p.sentiment === 'negative'
                ? PALETTE.negative
                : PALETTE.neutral,
          fontStyle: 'bold',
          halign: 'center',
        },
      },
      { content: fmt.int(p.likes), styles: { halign: 'right' } },
      { content: fmt.int(p.comments), styles: { halign: 'right' } },
      { content: fmt.int(p.shares), styles: { halign: 'right' } },
    ]),
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 2.2,
      textColor: PALETTE.body,
      lineColor: PALETTE.line,
      lineWidth: 0.1,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: PALETTE.primaryDk,
      textColor: PALETTE.white,
      fontSize: 7.5,
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: PALETTE.bgSoft },
    columnStyles: {
      0: { cellWidth: 7, halign: 'center', textColor: PALETTE.muted },
      1: { cellWidth: 30, fontStyle: 'bold' },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 20, halign: 'center' },
      4: { cellWidth: 18 },
      5: { cellWidth: 18 },
      6: { cellWidth: 18 },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  return doc.lastAutoTable.finalY + 6;
}

function renderCompetitors(doc, y, { context }) {
  const list = context?.competitors || [];
  if (!list.length) return y;

  y = sectionTitle(doc, y, {
    eyebrow: 'Competição',
    title: 'Comparativo de Perfis',
    description:
      'Ranking de perfis monitorados, ordenados por engajamento total.',
  });

  autoTable(doc, {
    startY: y,
    head: [
      ['#', 'Perfil', 'Plataforma', 'Tipo', 'Menções', '+', '-', '=', 'Engaj.'],
    ],
    body: list.map((c, i) => [
      String(i + 1),
      fmt.truncate(c.name, 30),
      c.platform,
      c.type === 'target' ? 'Alvo' : 'Competidor',
      {
        content: fmt.int(c.totalMentions),
        styles: { halign: 'right', fontStyle: 'bold' },
      },
      {
        content: fmt.int(c.sentiment?.positive),
        styles: { halign: 'right', textColor: PALETTE.positive },
      },
      {
        content: fmt.int(c.sentiment?.negative),
        styles: { halign: 'right', textColor: PALETTE.negative },
      },
      {
        content: fmt.int(c.sentiment?.neutral),
        styles: { halign: 'right', textColor: PALETTE.neutral },
      },
      {
        content: fmt.int(c.totalEngagement),
        styles: { halign: 'right', fontStyle: 'bold' },
      },
    ]),
    theme: 'grid',
    styles: {
      fontSize: 8.5,
      cellPadding: 2.2,
      textColor: PALETTE.body,
      lineColor: PALETTE.line,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: PALETTE.primaryDk,
      textColor: PALETTE.white,
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: PALETTE.bgSoft },
    columnStyles: {
      0: { cellWidth: 6, halign: 'center', textColor: PALETTE.muted },
      1: { cellWidth: 46, fontStyle: 'bold' },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  return doc.lastAutoTable.finalY + 6;
}

function renderMentioners(doc, y, { context }) {
  const list = context?.topMentioners || [];
  if (!list.length) return y;

  y = sectionTitle(doc, y, {
    eyebrow: 'Audiência',
    title: 'Top Mencionadores',
    description:
      'Usuários que mais mencionaram o perfil no período, com decomposição de sentimento.',
  });

  autoTable(doc, {
    startY: y,
    head: [['#', 'Autor', 'Total', 'Positivo', 'Negativo', 'Neutro']],
    body: list.slice(0, 15).map((m, i) => [
      String(i + 1),
      fmt.truncate(m.name || '-', 40),
      {
        content: fmt.int(m.total),
        styles: { halign: 'right', fontStyle: 'bold' },
      },
      {
        content: fmt.int(m.positive),
        styles: { halign: 'right', textColor: PALETTE.positive },
      },
      {
        content: fmt.int(m.negative),
        styles: { halign: 'right', textColor: PALETTE.negative },
      },
      {
        content: fmt.int(m.neutral),
        styles: { halign: 'right', textColor: PALETTE.neutral },
      },
    ]),
    theme: 'grid',
    styles: {
      fontSize: 8.5,
      cellPadding: 2.2,
      textColor: PALETTE.body,
      lineColor: PALETTE.line,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: PALETTE.primaryDk,
      textColor: PALETTE.white,
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: PALETTE.bgSoft },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center', textColor: PALETTE.muted },
      1: { cellWidth: 'auto', fontStyle: 'bold' },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  return doc.lastAutoTable.finalY + 6;
}

function renderInsights(doc, y, { context }) {
  const ins = context?.insights;
  if (!ins) return y;

  y = sectionTitle(doc, y, {
    eyebrow: 'Análise Estratégica',
    title: 'Insights Políticos',
  });

  if (ins.summary) {
    y = drawCallout(doc, y, {
      title: 'Síntese',
      body: ins.summary,
      color: PALETTE.primary,
      bg: [239, 246, 255],
    });
  }

  const drawInsightBlock = (label, items, color) => {
    if (!Array.isArray(items) || !items.length) return;
    y = ensureRoom(doc, y, 16);

    // Colored tab header
    const labelW = doc.getTextWidth(label) + 6;
    doc.setFillColor(...color);
    doc.roundedRect(MARGIN, y - 4, labelW, 6, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...PALETTE.white);
    doc.text(label.toUpperCase(), MARGIN + 3, y);
    y += 5;

    y = bulletList(doc, y, items, color);
    y += 2;
  };

  drawInsightBlock('Pontos Fortes', ins.positivePoints, PALETTE.positive);
  drawInsightBlock('Vulnerabilidades', ins.negativePoints, PALETTE.negative);
  drawInsightBlock(
    'Recomendações Estratégicas',
    ins.recommendations,
    PALETTE.primary
  );

  return y;
}

function renderMarkdownBody(doc, y, { report }) {
  if (!report?.content) return y;

  y = sectionTitle(doc, y, {
    eyebrow: 'Documento Completo',
    title: 'Análise Detalhada',
  });

  const lines = report.content.split('\n');
  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '');
    if (!line.trim()) {
      y += 2.5;
      continue;
    }

    let text = line;
    let font = 'normal';
    let size = 10;
    let color = PALETTE.body;
    let indent = 0;
    let spacing = 4.8;
    let topPad = 0;

    if (/^#\s/.test(line)) {
      text = line.replace(/^#\s/, '');
      font = 'bold';
      size = 16;
      color = PALETTE.ink;
      spacing = 7;
      topPad = 4;
    } else if (/^##\s/.test(line)) {
      text = line.replace(/^##\s/, '');
      font = 'bold';
      size = 13;
      color = PALETTE.primary;
      spacing = 6;
      topPad = 4;
    } else if (/^###\s/.test(line)) {
      text = line.replace(/^###\s/, '');
      font = 'bold';
      size = 11;
      color = PALETTE.ink;
      spacing = 5.5;
      topPad = 2;
    } else if (/^####\s/.test(line)) {
      text = line.replace(/^####\s/, '');
      font = 'bold';
      size = 10;
      color = PALETTE.ink;
      spacing = 5;
    } else if (/^[-*]\s/.test(line)) {
      text = `-  ${line.replace(/^[-*]\s/, '')}`;
      indent = 4;
    } else if (/^\d+\.\s/.test(line)) {
      indent = 4;
    } else if (line.startsWith('>')) {
      text = line.replace(/^>\s?/, '');
      color = PALETTE.muted;
      font = 'italic';
      indent = 4;
    } else if (/^\|.*\|.*\|/.test(line)) {
      // Skip md table rows (handled by marked would be overkill here)
      continue;
    }

    text = text
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/`(.+?)`/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

    doc.setFont('helvetica', font);
    doc.setFontSize(size);
    doc.setTextColor(...color);

    const wrapped = doc.splitTextToSize(text, CONTENT_W - indent);
    y = ensureRoom(doc, y, wrapped.length * spacing + topPad + 2);
    y += topPad;
    doc.text(wrapped, MARGIN + indent, y);
    y += wrapped.length * spacing;
  }

  return y;
}

// ───────────────────────────────────────────────────────────────────────────
// Entry point
// ───────────────────────────────────────────────────────────────────────────
export async function exportReportPdf({ report, profile, context }) {
  const doc = new jsPDF('p', 'mm', 'a4');

  // Cover
  renderCover(doc, { report, profile, context });

  // Executive summary - new page
  doc.addPage();
  let y = 20;
  y = renderExecutiveSummary(doc, y, { context });
  y = renderSentimentTable(doc, y, { context });

  // Timeline + topics
  y = renderSentimentTimeline(doc, y, { context });
  y = renderTopics(doc, y, { context });

  // Engagement
  y = renderTopPosts(doc, y, { context });

  // Comparative
  y = renderCompetitors(doc, y, { context });
  y = renderMentioners(doc, y, { context });

  // Insights
  y = renderInsights(doc, y, { context });

  // Full body
  y = renderMarkdownBody(doc, y, { report });

  // Apply header/footer chrome to all pages EXCEPT cover (page 1)
  const total = doc.internal.getNumberOfPages();
  for (let i = 2; i <= total; i += 1) {
    doc.setPage(i);
    drawPageChrome(doc, i - 1, total - 1, { profile, period: context?.period });
  }

  // Save
  const safeName = (report?.title || 'relatorio')
    .replace(/[^\w\d-\s]+/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 60);
  doc.save(`${safeName}.pdf`);
}
